/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import styles from '../styles/styles.module.css';

import { createUser, editUser } from '../../../api/user/post';
import { addUser } from '../../../Redux/slices/allUsers';
import { setApprovedSites } from '../../../Redux/slices/userSlice';

interface AddUserInterface {
  setAddUserActive: React.Dispatch<React.SetStateAction<boolean>>;
  mode?: 'create' | 'edit';
  initialUser?: Partial<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    assignedSites: any[];
    companyPermissions: Record<string, any>;
    sitePermissions: Record<string, any>;
    userType: string;
  }>;
}

type CompanyPermKey =
  | 'enrollSites'
  | 'deleteSites'
  | 'editSites'
  | 'viewSites'
  | 'enrollUsers'
  | 'deleteUsers'
  | 'editUsers'
  | 'viewUsers'
  | 'addFiles'
  | 'createLayouts'
  | 'editLayouts'
  | 'deleteLayouts'
  | 'viewLayouts';

type SitePermKey =
  | 'editSites'
  | 'viewSites'
  | 'enrollUsers'
  | 'deleteUsers'
  | 'editUsers'
  | 'viewUsers'
  | 'addFiles'
  | 'createLayouts'
  | 'editLayouts'
  | 'deleteLayouts'
  | 'viewLayouts';

const EMPTY_COMPANY: Record<CompanyPermKey, boolean> = {
  enrollSites: false,
  deleteSites: false,
  editSites: false,
  viewSites: false,
  enrollUsers: false,
  deleteUsers: false,
  editUsers: false,
  viewUsers: false,
  addFiles: false,
  createLayouts: false,
  editLayouts: false,
  deleteLayouts: false,
  viewLayouts: false,
};
const EMPTY_SITE: Record<SitePermKey, boolean> = {
  editSites: false,
  viewSites: false,
  enrollUsers: false,
  deleteUsers: false,
  editUsers: false,
  viewUsers: false,
  addFiles: false,
  createLayouts: false,
  editLayouts: false,
  deleteLayouts: false,
  viewLayouts: false,
};

const COMPANY_KEYS = Object.keys(EMPTY_COMPANY) as CompanyPermKey[];
const SITE_KEYS = Object.keys(EMPTY_SITE) as SitePermKey[];

const COMPANY_TO_SITE: Partial<Record<CompanyPermKey, SitePermKey>> = {
  editSites: 'editSites',
  viewSites: 'viewSites',
  enrollUsers: 'enrollUsers',
  deleteUsers: 'deleteUsers',
  editUsers: 'editUsers',
  viewUsers: 'viewUsers',
  addFiles: 'addFiles',
  createLayouts: 'createLayouts',
  editLayouts: 'editLayouts',
  deleteLayouts: 'deleteLayouts',
  viewLayouts: 'viewLayouts',
};

const SITE_TO_COMPANY: Partial<Record<SitePermKey, CompanyPermKey>> =
  Object.entries(COMPANY_TO_SITE).reduce(
    (acc, [ck, sk]) => {
      if (sk) (acc as any)[sk] = ck;
      return acc;
    },
    {} as Partial<Record<SitePermKey, CompanyPermKey>>
  );

const toIds = (arr?: any[]): string[] =>
  (arr ?? [])
    .map((s) => (typeof s === 'string' ? s : s?._id || s?.id || ''))
    .filter(Boolean);

const coerceBool = (v: any) => v === true || v === 'true';

const mergePerms = <T extends string>(
  base: Record<T, boolean>,
  incoming?: Record<string, any>
): Record<T, boolean> => {
  const m = { ...base };
  if (incoming) {
    (Object.keys(base) as T[]).forEach((k) => {
      if (k in incoming) m[k] = coerceBool(incoming[k]);
    });
  }
  return m;
};

const AddUser: React.FC<AddUserInterface> = ({
  setAddUserActive,
  mode = 'create',
  initialUser,
}) => {
  const dispatch = useDispatch();
  const { sites } = useSelector((state: any) => state.associatedSites);
  const userCompPerms = useSelector(
    (state: any) => state.user.companyPermissions
  );
  const userSitePerms = useSelector((state: any) => state.user.sitePermissions);
  const [firstName, setFirstName] = useState(initialUser?.firstName ?? '');
  const [lastName, setLastName] = useState(initialUser?.lastName ?? '');
  const [email, setEmail] = useState(initialUser?.email ?? '');
  const [phone, setPhone] = useState(initialUser?.phone ?? '');
  const [isCompanyAdmin, setIsCompanyAdmin] = useState<boolean>(
    initialUser?.userType === 'COMPANY_ADMIN' ? true : false
  );
  const [assignedSites, setAssignedSites] = useState<string[]>(
    toIds(initialUser?.assignedSites as any[])
  );
  const { currentSite } = useSelector((state: any) => state);

  const [companyPermissions, setCompanyPermissions] = useState<
    Record<CompanyPermKey, boolean>
  >(mergePerms(EMPTY_COMPANY, initialUser?.companyPermissions));
  const [sitePermissions, setSitePermissions] = useState<
    Record<SitePermKey, boolean>
  >(mergePerms(EMPTY_SITE, initialUser?.sitePermissions));

  // Initialize master toggles based on whether data exists
  const [canGrantCompanyPermissions, setCanGrantCompanyPermissions] =
    useState<boolean>(
      Object.values(initialUser?.companyPermissions || {}).some(Boolean) ||
        isCompanyAdmin
    );
  const [canGrantSitePermissions, setCanGrantSitePermissions] =
    useState<boolean>(
      Object.values(initialUser?.sitePermissions || {}).some(Boolean) ||
        isCompanyAdmin
    );
  const [loading, setLoading] = useState(false);
  const { _id, userType } = useSelector((state: any) => state.user);
  const [mutableSites, setMutableSites] = useState<any>([]);

  useSelector((state: any) => console.log(state));

  //determines if an a user can assign employees company wide
  function unrestrictedSitePermission(): String | Boolean {
    if (userType === 'HYPER_ADMIN' || userType === 'COMPANY_ADMIN') {
      return 'FULL';
    } else {
      if (userCompPerms.enrollUsers === true) {
        return 'FULL';
      } else if (userSitePerms.enrollUsers === true) {
        return 'PARTIAL';
      } else {
        return false;
      }
    }
  }

  useEffect(() => {
    if (unrestrictedSitePermission() === 'PARTIAL') {
      const modifiedSites = sites.filter(
        (site: any) => site.id === currentSite.siteId
      );
      setMutableSites(modifiedSites);
    } else if (unrestrictedSitePermission() === 'FULL') {
      setMutableSites(sites);
    }
  }, []);

  // --- HYDRATION ONLY ---
  // Only runs when mode or ID changes, NOT when permissions change
  useEffect(() => {
    if (mode !== 'edit' || !initialUser) return;

    setFirstName(initialUser.firstName ?? '');
    setLastName(initialUser.lastName ?? '');
    setEmail(initialUser.email ?? '');
    setPhone(initialUser.phone ?? '');
    setAssignedSites(toIds(initialUser.assignedSites as any[]));

    const nextCompany = mergePerms(
      EMPTY_COMPANY,
      initialUser.companyPermissions
    );
    const nextSite = mergePerms(EMPTY_SITE, initialUser.sitePermissions);

    setCompanyPermissions(nextCompany);
    setSitePermissions(nextSite);

    // Set master toggles if permissions exist
    setCanGrantCompanyPermissions(Object.values(nextCompany).some(Boolean));
    setCanGrantSitePermissions(Object.values(nextSite).some(Boolean));
  }, [mode, initialUser?._id]);
  // Removed deep permission dependencies here to prevent loops.
  // We trust initialUser object identity or _id to trigger reload.

  const fmtPhone = (v: string) => {
    const c = v.replace(/\D/g, '').substring(0, 10);
    if (c.length < 4) return c;
    if (c.length < 7) return `(${c.slice(0, 3)}) ${c.slice(3)}`;
    return `(${c.slice(0, 3)}) ${c.slice(3, 6)}-${c.slice(6)}`;
  };

  //user can only set permissions on somebody that is not himself
  function canSetPermissions() {
    return _id !== initialUser?._id;
  }

  // --- HANDLERS (Manual User Interaction) ---

  const handleMasterCompanyToggle = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = e.target.checked;
    setCanGrantCompanyPermissions(isChecked);
    // Only clear if user physically unchecks the box
    if (!isChecked) {
      setCompanyPermissions({ ...EMPTY_COMPANY });
    }
  };

  const handleMasterSiteToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setCanGrantSitePermissions(isChecked);
    // Only clear if user physically unchecks the box
    if (!isChecked) {
      setSitePermissions({ ...EMPTY_SITE });
    }
  };

  const toggleCompany = (k: CompanyPermKey) => {
    setCompanyPermissions((prev) => {
      const isTurningOn = !prev[k];

      // Auto-promote: If turning Company ON, turn Site ON too
      if (isTurningOn) {
        const sk = COMPANY_TO_SITE[k];
        if (sk) {
          setSitePermissions((prevSite) => ({ ...prevSite, [sk]: true }));
        }
      }

      return { ...prev, [k]: isTurningOn };
    });
  };

  const toggleSite = (k: SitePermKey) => {
    setSitePermissions((p) => ({ ...p, [k]: !p[k] }));
  };

  const isSiteDisabled = (k: SitePermKey) => {
    const ck = SITE_TO_COMPANY[k];
    return !!(canGrantCompanyPermissions && ck && companyPermissions[ck]);
  };

  const validate = () => {
    if (!firstName || !lastName || !email || !phone) {
      alert(
        'All fields are required: First name, Last name, Email, and Phone.'
      );
      return false;
    }
    if (!assignedSites.length) {
      alert('User must be assigned to at least one site.');
      return false;
    }
    return true;
  };

  const payload = () => ({
    firstName,
    lastName,
    email,
    phone,
    assignedSites,
    userType: 'USER',
    companyPermissions,
    sitePermissions,
  });

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (mode === 'edit' && initialUser?._id) {
        const res = await editUser(initialUser._id, payload());

        if (res?.success) {
          window.location.reload();
        } else alert(res?.msg ?? 'Failed to update user.');
      } else {
        const res = await createUser(payload());
        if (res?.success) {
          dispatch(addUser(res.data));
          setAddUserActive(false);
        } else alert(res?.msg ?? 'Failed to create user.');
      }
    } catch (e) {
      console.error(e);
      alert(
        mode === 'edit' ? 'Error updating user.' : 'Error submitting user.'
      );
    } finally {
      setLoading(false);
    }
  };

  const labelFor = (k: string) => {
    switch (k) {
      case 'enrollSites':
        return 'Enroll Sites';
      case 'deleteSites':
        return 'Delete Sites';
      case 'editSites':
        return 'Edit Sites';
      case 'viewSites':
        return 'View Sites';
      case 'enrollUsers':
        return 'Enroll Users';
      case 'deleteUsers':
        return 'Delete Users';
      case 'editUsers':
        return 'Edit Users';
      case 'viewUsers':
        return 'View Users';
      case 'addFiles':
        return 'Add Files';
      case 'createLayouts':
        return 'New Layouts';
      case 'deleteLayouts':
        return 'Delete Layouts';
      case 'editLayouts':
        return 'Edit Layouts';
      case 'viewLayouts':
        return 'View Layouts';
      default:
        return k;
    }
  };

  function displayCompanyAdminCheckbox() {
    return userType === 'HYPER_ADMIN' || userType === 'COMPANY_ADMIN';
  }

  function handleCompanyAdminToggle() {
    const newValue = !isCompanyAdmin;
    setIsCompanyAdmin(newValue);
    if (newValue) {
      //if checking company admin, set all permissions to true
      setCompanyPermissions({ ...EMPTY_COMPANY });
      setSitePermissions({ ...EMPTY_SITE });
      setCanGrantCompanyPermissions(true);
      setCanGrantSitePermissions(true);
    }
  }

  return (
    <div className={styles.addSiteContainer}>
      <div className={styles.addSiteWrapper}>
        {/* Left column: details */}
        <div className={styles.addSiteLeft}>
          <div className={styles.addSiteLeft_left}>
            <div className={styles.inputContainer}>
              <div className={styles.required}>
                <p className={styles.label}>First Name</p>
                <p className={styles.labelAsterix}>*</p>
              </div>
              <input
                className={styles.inputElement}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className={styles.inputContainer}>
              <div className={styles.required}>
                <p className={styles.label}>Last Name</p>
                <p className={styles.labelAsterix}>*</p>
              </div>
              <input
                className={styles.inputElement}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            {displayCompanyAdminCheckbox() && (
              <div className={styles.inputContainer}>
                <div className={styles.hyperAdminCheckboxContainer}>
                  <input
                    checked={isCompanyAdmin}
                    type="checkbox"
                    onChange={(e) => handleCompanyAdminToggle()}
                  />
                  <p className={styles.checkboxLabel}>Company Admin</p>
                </div>
              </div>
            )}
          </div>

          <div className={styles.addSiteLeft_right}>
            <div className={styles.inputContainer}>
              <div className={styles.required}>
                <p className={styles.label}>Email</p>
                <p className={styles.labelAsterix}>*</p>
              </div>
              <input
                className={styles.inputElement}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className={styles.inputContainer}>
              <p className={styles.label}>Phone</p>
              <input
                className={styles.inputElement}
                value={phone}
                onChange={(e) => setPhone(fmtPhone(e.target.value))}
              />
            </div>

            {/* Approved Sites */}
            <div className={styles.inputContainer}>
              <p className={styles.label}>Approved Sites</p>
              <select
                className={styles.inputElement}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id && !assignedSites.includes(id))
                    setAssignedSites([...assignedSites, id]);
                  e.target.value = '';
                }}
              >
                <option value="">Select a site...</option>
                {mutableSites.map((s: any) => (
                  <option key={s._id} value={s._id}>
                    {s.siteName}
                  </option>
                ))}
              </select>

              <div className={styles.selectedChipsContainer}>
                {assignedSites.map((id) => {
                  const s = sites.find((x: any) => x._id === id);

                  return (
                    <div key={id} className={styles.siteChip}>
                      <span>{s?.siteName ?? id}</span>
                      <button
                        disabled={currentSite.siteId !== id}
                        style={
                          currentSite.siteId !== id
                            ? { visibility: 'hidden' }
                            : {}
                        }
                        className={styles.siteChipClose}
                        type="button"
                        onClick={() =>
                          setAssignedSites(
                            assignedSites.filter((x) => x !== id)
                          )
                        }
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Right column: permissions */}
        <div className={styles.addSiteRight}>
          {canSetPermissions() && (
            <div className={styles.profileCard}>
              <p className={styles.permissionsHeadline}>Admin Permissions</p>
              {(userType === 'COMPANY_ADMIN' || userType === 'HYPER_ADMIN') && (
                <div className={styles.smallBoxHr1}></div>
              )}

              {/* Company Level */}
              {(userType === 'COMPANY_ADMIN' || userType === 'HYPER_ADMIN') && (
                <div className={styles.levelHeadline}>
                  <input
                    type="checkbox"
                    className={styles.levelCheckbox}
                    disabled={isCompanyAdmin}
                    checked={isCompanyAdmin ? true : canGrantCompanyPermissions}
                    onChange={handleMasterCompanyToggle}
                  />
                  <p className={styles.permissionSubHeadline}>Company Level</p>
                </div>
              )}

              {(userType === 'COMPANY_ADMIN' || userType === 'HYPER_ADMIN') &&
                canGrantCompanyPermissions && (
                  <div className={styles.subCategory}>
                    <div className={styles.checkboxHoldingDualContainer}>
                      <div className={styles.checkboxHoldingContainer}>
                        {COMPANY_KEYS.slice(
                          0,
                          Math.ceil(COMPANY_KEYS.length / 2)
                        ).map((k) => (
                          <div key={k} className={styles.labelAndCheckbox}>
                            <input
                              type="checkbox"
                              disabled={isCompanyAdmin}
                              checked={
                                isCompanyAdmin
                                  ? true
                                  : Boolean(companyPermissions[k])
                              }
                              onChange={() => toggleCompany(k)}
                            />
                            <p className={styles.checkboxLabel}>
                              {labelFor(k)}
                            </p>
                          </div>
                        ))}
                      </div>
                      <div className={styles.checkboxHoldingContainer}>
                        {COMPANY_KEYS.slice(
                          Math.ceil(COMPANY_KEYS.length / 2)
                        ).map((k) => (
                          <div key={k} className={styles.labelAndCheckbox}>
                            <input
                              type="checkbox"
                              disabled={
                                !canGrantCompanyPermissions || isCompanyAdmin
                              }
                              checked={
                                isCompanyAdmin
                                  ? true
                                  : Boolean(companyPermissions[k])
                              }
                              onChange={() => toggleCompany(k)}
                            />
                            <p className={styles.checkboxLabel}>
                              {labelFor(k)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              <div className={styles.smallBoxHr2}></div>

              {/* Site Level */}
              <div className={styles.levelHeadline}>
                <input
                  type="checkbox"
                  className={styles.levelCheckbox}
                  disabled={isCompanyAdmin}
                  checked={isCompanyAdmin ? true : canGrantSitePermissions}
                  // Updated handler used here
                  onChange={handleMasterSiteToggle}
                />
                <p className={styles.permissionSubHeadline}>Site Level</p>
              </div>

              {canGrantSitePermissions && (
                <div className={styles.subCategory}>
                  <div className={styles.checkboxHoldingDualContainer}>
                    <div className={styles.checkboxHoldingContainer}>
                      {SITE_KEYS.slice(0, Math.ceil(SITE_KEYS.length / 2)).map(
                        (k) => (
                          <div key={k} className={styles.labelAndCheckbox}>
                            <input
                              type="checkbox"
                              disabled={
                                !canGrantSitePermissions ||
                                isSiteDisabled(k) ||
                                isCompanyAdmin
                              }
                              checked={
                                isCompanyAdmin
                                  ? true
                                  : Boolean(sitePermissions[k])
                              }
                              onChange={() => toggleSite(k)}
                            />
                            <p className={styles.checkboxLabel}>
                              {labelFor(k)}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                    <div className={styles.checkboxHoldingContainer}>
                      {SITE_KEYS.slice(Math.ceil(SITE_KEYS.length / 2)).map(
                        (k) => (
                          <div key={k} className={styles.labelAndCheckbox}>
                            <input
                              type="checkbox"
                              disabled={
                                !canGrantSitePermissions ||
                                isSiteDisabled(k) ||
                                isCompanyAdmin
                              }
                              checked={
                                isCompanyAdmin
                                  ? true
                                  : Boolean(sitePermissions[k])
                              }
                              onChange={() => toggleSite(k)}
                            />
                            <p className={styles.checkboxLabel}>
                              {labelFor(k)}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      <button
        onClick={handleSubmit}
        className={styles.createSiteBtn}
        disabled={loading}
      >
        {loading ? (
          <PulseLoader style={{ marginTop: '10px' }} color="white" size={5} />
        ) : mode === 'edit' ? (
          'Submit Changes'
        ) : (
          'Create User'
        )}
      </button>
    </div>
  );
};

export default AddUser;
